import { useState } from 'react';
import { Linking, Platform, Pressable, useColorScheme } from 'react-native';
import { XStack, YStack } from 'tamagui';

import { AuthenticatedImage } from '@/components/authenticated-image';
import { VideoEmbedCard } from '@/components/video-embed-card';
import { Text } from '@/components/text';
import type { Attachment } from '@/lib/api';
import { embedPlayerUrl } from '@/lib/video-embed';
import { borderWidth, darkColors, lightColors, radius } from '@/theme/tokens.generated';

// A shared link as a card: thumbnail, domain, title, description (the
// iMessage/Slack shape). The server built it from the page, so every string
// here is untrusted page text -- rendered as plain Text, never as markup.
//
// `compact` is the feed form: a small square thumbnail beside the text, no
// description, so a card in a long list stays one glance tall.
//
// The whole card opens the link in the system browser. Inside a pressable
// thread card the press must not also navigate that card, hence
// stopPropagation.
export function LinkPreviewCard({
  attachment,
  compact = false,
  width,
}: {
  attachment: Attachment;
  compact?: boolean;
  // Explicit width for shrink-to-fit parents (a message bubble); never
  // wider than the container. Omit to fill it (feed and detail).
  width?: number;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const colors = useColorScheme() === 'dark' ? darkColors : lightColors;

  const domain = attachment.domain ?? attachment.url;
  const title = attachment.title ?? domain;

  // A video on web plays in place (after a tap). Native has no webview yet,
  // and the compact feed form stays a plain card, so both fall through to
  // opening the link.
  const playerUrl = attachment.embed && !compact && Platform.OS === 'web' ? embedPlayerUrl(attachment.embed) : null;

  if (playerUrl) {
    return <VideoEmbedCard attachment={attachment} playerUrl={playerUrl} width={width} />;
  }

  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`Link preview: ${title}, ${domain}`}
      accessibilityHint="Opens in your browser"
      onPress={(event) => {
        event.stopPropagation();
        // The server and database already guarantee http(s); never trust
        // that here, since this opens whatever string arrives.
        if (/^https?:\/\//i.test(attachment.url)) void Linking.openURL(attachment.url);
      }}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ pressed }) => [
        { borderRadius: radius.md, alignSelf: 'stretch', maxWidth: width ? '100%' : 480 },
        width ? { width } : null,
        focused
          ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor: colors.actionPrimary, outlineOffset: 2 }
          : null,
        pressed ? { opacity: 0.85 } : null,
      ]}
    >
      <YStack
        backgroundColor="$card"
        borderWidth={borderWidth.hairline}
        borderColor={hovered ? '$colorLink' : '$borderColor'}
        borderRadius="$lg"
        overflow="hidden"
      >
        {compact ? (
          <CompactBody attachment={attachment} title={title} domain={domain} />
        ) : (
          <FullBody attachment={attachment} title={title} domain={domain} />
        )}
      </YStack>
    </Pressable>
  );
}

function FullBody({ attachment, title, domain }: { attachment: Attachment; title: string; domain: string }) {
  return (
    <YStack>
      {attachment.imageUrl && (
        <AuthenticatedImage
          path={attachment.imageUrl}
          accessibilityLabel={`Preview image for ${title}`}
          width="100%"
          aspectRatio={1.91}
          objectFit="cover"
        />
      )}
      <YStack gap="$1" p="$3">
        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" numberOfLines={1}>
          {domain}
        </Text>
        <Text color="$color" fontWeight="$semibold" numberOfLines={2}>
          {title}
        </Text>
        {attachment.description && (
          <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" numberOfLines={3}>
            {attachment.description}
          </Text>
        )}
      </YStack>
    </YStack>
  );
}

function CompactBody({ attachment, title, domain }: { attachment: Attachment; title: string; domain: string }) {
  return (
    <XStack gap="$3" alignItems="center">
      {attachment.imageUrl && (
        <AuthenticatedImage
          path={attachment.imageUrl}
          accessibilityLabel={`Preview image for ${title}`}
          width={72}
          aspectRatio={1}
          objectFit="cover"
        />
      )}
      <YStack flex={1} gap="$1" py="$2" pr="$3" pl={attachment.imageUrl ? 0 : '$3'} minWidth={0}>
        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" numberOfLines={1}>
          {domain}
        </Text>
        <Text color="$color" fontWeight="$semibold" numberOfLines={2}>
          {title}
        </Text>
      </YStack>
    </XStack>
  );
}
